import { useProjectId } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import { Grid, Icon, Tooltip, UseFormReturn } from '@gcl/react-core';
import { RowState } from '@gcl/react-core/dist/components/Table/UnstyledTableComponents/Layout/VerticalTableLayout';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';

import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { LOCAL_STORAGE_KEYS } from '@common/constants';
import { IProjectTransmittalItem } from '@transmittal/transmittalItem/transmittalItem.models';
import {
  useUpdateProjectTransmittalItemRemoveFromUse,
  useUpdateProjectTransmittalItemReuse,
} from '@transmittal/transmittalItem/transmittalItem.queries';

interface ITransmittalItemTableRowInUseProps extends Omit<ITransmittalItemTableRowInUseIcon, 'onClickToggleInUse'> {
  isAnyActionLoading: boolean;
  setRowUploading?: (index: number, isUploading: boolean) => void;
  queryKey: QueryKey;

  useFormMethods: UseFormReturn;
  setRowState: Dispatch<SetStateAction<RowState>> | undefined;
}

export const TransmittalItemTableRowInUse = ({
  item,
  transmittalStatus,
  transmittalType,

  transmittalId,
  isAnyActionLoading,
  setRowUploading,

  queryKey,
  useFormMethods,
  rowIndex,
  setRowState,
  rowState,
}: ITransmittalItemTableRowInUseProps) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  const { renderModal: renderConfirmationPortal, openPortal: openConfirmationPortal } = useConfirmationModal({
    content: item.form?.item?.inUse
      ? 'This action is to lock file from update and approve in this T1 package. It will NOT delete the document register record from the transmittal. Do you wish to continue?'
      : 'This action is to unlock file from update and approve in this T1 package. Do you wish to continue?',
    onConfirm: () => {
      onToggleInUse();
    },
    isShowDoNotShowAgain: true,
    modalId: item.form?.item?.inUse
      ? LOCAL_STORAGE_KEYS.DO_NOT_SHOW_TRANSMITTAL_ITEM_NOT_IN_USE_MODAL_AGAIN
      : LOCAL_STORAGE_KEYS.DO_NOT_SHOW_TRANSMITTAL_ITEM_REUSE_MODAL_AGAIN,
  });

  const {
    mutate: mutateRemoveFromUse,
    isSuccess: isSuccessRemoveFromUse,
    reset: resetRemoveFromUse,
    error: errorRemoveFromUse,
    isError: isErrorRemoveFromUser,
    isLoading: isLoadingRemoveFromUse,
    data: dataRemoveFromUse,
  } = useUpdateProjectTransmittalItemRemoveFromUse();
  const {
    mutate: mutateReuse,
    isSuccess: isSuccessReuse,
    reset: resetReuse,
    error: errorReuse,
    isError: isErrorReuse,
    isLoading: isLoadingReuse,
    data: dataReuse,
  } = useUpdateProjectTransmittalItemReuse();

  React.useEffect(() => {
    if (!isSuccessRemoveFromUse && !isSuccessReuse) return;
    if (!useFormMethods) return;
    const isInUse = !dataRemoveFromUse;
    if (setRowUploading) setRowUploading(rowIndex, false);

    const prevDataList: IProjectTransmittalItem[] = queryClient.getQueryData(queryKey) ?? [];
    const prevDataIndex = prevDataList.findIndex((pd) => pd.id === item.form?.item?.id);
    if (prevDataIndex >= 0) {
      const prevData = prevDataList[prevDataIndex];
      const newData: IProjectTransmittalItem = {
        ...prevData,
        inUse: isInUse,
      };

      const newDataList = [...prevDataList];
      newDataList[prevDataIndex] = newData;
      queryClient.setQueryData(queryKey, newDataList);
    }

    queryClient.invalidateQueries(queryKey);
    if (setRowState) setRowState('Idle');
    if (isSuccessRemoveFromUse) resetRemoveFromUse();
    if (isSuccessReuse) resetReuse();
  }, [isSuccessRemoveFromUse, isSuccessReuse]);

  const onClickToggleInUse = (
    e: React.MouseEvent<SVGElement, MouseEvent> | React.MouseEventHandler<SVGElement> | undefined,
  ) => {
    openConfirmationPortal(e);
  };
  const onToggleInUse = useCallback(() => {
    if (!item.form?.uuid) return;
    if (setRowUploading) setRowUploading(rowIndex, true);
    if (setRowState) setRowState('Loading');

    const currentIsInUse = item.form.item?.inUse;
    if (currentIsInUse) {
      mutateRemoveFromUse({
        transmittalId,
        transmittalItemId: item.form.item?.id ?? 0,
        props: { projectId },
      });
      return;
    }
    mutateReuse({
      transmittalId,
      transmittalItemId: item.form.item?.id ?? 0,
      props: { projectId },
    });
  }, [JSON.stringify(item.form), transmittalId, projectId]);

  if (!useFormMethods) return null;
  if (item.form?.projectFile && item.form?.projectFile?.uploadState !== 'finish') return null;

  return (
    <>
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          {
            error: errorRemoveFromUse,
            genericError: `Remove from use failed`,
            reset: resetRemoveFromUse,
          },
          {
            error: errorReuse,
            genericError: 'Reuse failed',
            reset: resetReuse,
          },
        ]}
        callbackAfterToast={() => {
          if (setRowUploading) setRowUploading(rowIndex, false);
          if (setRowState) setRowState('Idle');
        }}
      />
      {rowState === 'Idle' && item.form?.item?.type === transmittalType && (
        <TransmittalItemTableRowInUseIcon
          item={item}
          transmittalId={transmittalId}
          transmittalStatus={transmittalStatus}
          transmittalType={transmittalType}
          rowIndex={rowIndex}
          rowState={rowState}
          onClickToggleInUse={transmittalStatus === ETransmittalStatus.DRAFT ? onClickToggleInUse : undefined}
        />
      )}
      {renderConfirmationPortal(
        isLoadingReuse || isLoadingRemoveFromUse,
        isErrorReuse || isErrorRemoveFromUser,
        isSuccessReuse || isSuccessRemoveFromUse,
      )}
    </>
  );
};

interface ITransmittalItemTableRowInUseIcon {
  item: IFormFileDrawingItem;
  transmittalType?: ETransmittalType;
  transmittalId: number;
  transmittalStatus?: ETransmittalStatus;
  rowIndex: number;
  rowState: RowState | undefined;
  onClickToggleInUse:
    | ((e: React.MouseEvent<SVGElement, MouseEvent> | React.MouseEventHandler<SVGElement> | undefined) => void)
    | undefined;
}
export const TransmittalItemTableRowInUseIcon = ({
  item,
  transmittalId,
  transmittalStatus,
  transmittalType,
  rowIndex,
  rowState,
  onClickToggleInUse,
}: ITransmittalItemTableRowInUseIcon) => {
  if (!!item.form?.projectFile && item.form?.projectFile?.uploadState !== 'finish') return null;

  if (rowState !== 'Idle' || item.form?.item?.type !== transmittalType) return <></>;

  const iconInUse = (
    <Icon
      iconName="RecordCircle"
      data-testid={`inUse-${rowIndex}`}
      className={`${transmittalStatus === ETransmittalStatus.DRAFT ? 'gcl-ic-btn-lg' : 'gcl-ic-lg'} ${
        item.form?.item?.inUse ? 'gcl-fc-success-400' : 'gcl-fc-danger-400'
      }`}
      onClick={onClickToggleInUse}
    />
  );
  return (
    <Grid item>
      {transmittalStatus === ETransmittalStatus.APPROVED && item.transmittalItem?.inUse && iconInUse}
      {transmittalStatus === ETransmittalStatus.APPROVED && !item.transmittalItem?.inUse && (
        <Tooltip
          overlay={`This item is removed from use by ${item.transmittalItem?.lastTransmittal?.transmittalNo} ${
            item.transmittalItem?.lastTransmittalId === transmittalId ? '(this transmittal)' : ''
          }`}
        >
          {iconInUse}
        </Tooltip>
      )}
      {transmittalStatus !== ETransmittalStatus.APPROVED && (
        <Tooltip
          overlay={
            item.form?.item?.inUse
              ? 'Remove from use'
              : `Mark in use (This item is removed from use by ${
                  item.transmittalItem?.lastTransmittal?.transmittalNo
                } ${item.transmittalItem?.lastTransmittalId === transmittalId ? '(this transmittal)' : ''})`
          }
          id="remove-from-use"
          key="remove-from-use"
        >
          {iconInUse}
        </Tooltip>
      )}
    </Grid>
  );
};
